/**
 * Forgot Password Page
 *
 * Allows users to request a password reset email.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, CheckCircle, Mail } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { BrandMark, BrandWordmark } from '../components/brand-mark'
import { Alert, Button, buttonClasses, cn, TextField } from '../components/ui'
import { requestPasswordReset } from '../lib/auth-client'

const createSchema = (t: (key: string) => string) =>
	z.object({
		email: z.string().email(t('auth.validation.invalidEmail')),
	})

type FormData = z.infer<ReturnType<typeof createSchema>>

export default function ForgotPasswordPage() {
	const { t } = useTranslation()
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [sent, setSent] = useState(false)

	const schema = createSchema(t)
	const form = useForm<FormData>({
		resolver: zodResolver(schema),
	})
	const { setFocus } = form
	// Move focus to the success heading once the email is sent so the screen
	// reader announces the confirmation that replaces the form.
	const successHeadingRef = useRef<HTMLHeadingElement>(null)

	useEffect(() => {
		if (sent) successHeadingRef.current?.focus()
	}, [sent])

	const handleSubmit = async (data: FormData) => {
		setIsLoading(true)
		setError(null)

		try {
			const result = await requestPasswordReset({
				email: data.email,
				redirectTo: '/auth/reset-password',
			})

			if (result.error) {
				setError(result.error.message ?? t('auth.errors.resetFailed'))
				return
			}

			setSent(true)
		} catch {
			setError(t('auth.errors.resetFailed'))
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-screen bg-base-200 flex flex-col">
			<header className="bg-base-100 border-b border-base-300 py-4">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center gap-4">
						<Link
							to="/auth"
							className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
							aria-label={t('common.back')}
						>
							<ArrowLeft size={20} />
						</Link>
						<Link to="/" className="flex items-center gap-2">
							<BrandMark />
							<BrandWordmark />
						</Link>
					</div>
				</div>
			</header>

			<main className="flex-1 flex items-center justify-center p-4">
				<div className="card-refined w-full max-w-md p-8">
					{sent ? (
						// biome-ignore lint/a11y/useSemanticElements: status live region, not an <output>
						<div className="text-center" role="status">
							<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
								<CheckCircle size={32} className="text-success" />
							</div>
							<h1
								ref={successHeadingRef}
								tabIndex={-1}
								className="text-2xl font-display mb-2 outline-none"
							>
								{t('auth.resetEmailSent')}
							</h1>
							<p className="text-base-content/60 mb-6">{t('auth.resetEmailSentDescription')}</p>
							<Link to="/auth" className={cn(buttonClasses(), 'inline-flex items-center gap-2')}>
								<ArrowLeft size={16} />
								{t('auth.backToLogin')}
							</Link>
						</div>
					) : (
						<>
							<div className="text-center mb-8">
								<h1 className="text-2xl font-display mb-2">{t('auth.forgotPassword')}</h1>
								<p className="text-base-content/60">{t('auth.forgotPasswordDescription')}</p>
							</div>

							{error && (
								<Alert tone="error" className="mb-6">
									{error}
								</Alert>
							)}

							<form
								onSubmit={form.handleSubmit(handleSubmit, () => setFocus('email'))}
								className="space-y-4"
							>
								<TextField
									id="reset-email"
									type="email"
									label={t('auth.email')}
									placeholder={t('auth.emailPlaceholder')}
									leadingIcon={<Mail size={18} />}
									autoComplete="email"
									required
									error={form.formState.errors.email?.message}
									{...form.register('email')}
								/>

								<Button type="submit" loading={isLoading} variant="primary" block>
									{isLoading ? t('auth.sending') : t('auth.sendResetLink')}
								</Button>
							</form>

							<div className="mt-6 text-center">
								<Link to="/auth" className="text-primary hover:underline font-medium">
									{t('auth.backToLogin')}
								</Link>
							</div>
						</>
					)}
				</div>
			</main>
		</div>
	)
}
